/**
 * V10 Phase C — 🔗 "이 연결 끊기" Modal.
 *
 * Link 단위 무력화 — 이 edge 에서만 해당 episode 비활성. 다른 edge 에는 영향 없음.
 * 카운트다운 2초 · reason 선택.
 */
import { ConfirmCountdownModal } from "./ConfirmCountdownModal.tsx"

type Props = {
	readonly fromId: string
	readonly toId: string
	readonly relation: string
	readonly episodeContent: string
	readonly onClose: () => void
	readonly onConfirm: (reason?: string) => Promise<void>
}

export function EdgeInvalidateLinkModal(props: Props) {
	return (
		<ConfirmCountdownModal
			title="🔗 이 연결 끊기"
			bodyCopy="이 edge 에서만 해당 episode 를 비활성화합니다. 다른 edge 에는 영향 없습니다."
			bodySlot={
				<div style={{ display: "flex", flexDirection: "column", gap: "var(--pk-space-3)" }}>
					<div
						style={{
							background: "var(--pk-warning-soft)",
							border: "1px solid var(--pk-warning-border)",
							borderRadius: "var(--pk-radius-md)",
							padding: "var(--pk-space-3)",
							fontSize: "0.85rem",
							color: "var(--pk-text-primary)",
							lineHeight: 1.5
						}}
					>
						이 <strong>edge 에서만</strong> 해당 episode 를 비활성화합니다. 다른 edge 에는 영향이 없습니다.
					</div>

					<ModalSectionCard label="대상 관계">
						<div className="shape-flow shape-flow--vertical" style={{ margin: 0 }}>
							<NodeChip id={props.fromId} />
							<div className="shape-flow__rel">
								<span className="shape-flow__arrow shape-flow__arrow--down" aria-hidden>
									↓
								</span>
								<span className="shape-flow__rel-label">{props.relation}</span>
								<span className="shape-flow__arrow shape-flow__arrow--down" aria-hidden>
									↓
								</span>
							</div>
							<NodeChip id={props.toId} />
						</div>
					</ModalSectionCard>

					<ModalSectionCard label="대상 에피소드">
						<div
							style={{
								fontSize: "0.85rem",
								color: "var(--pk-text-primary)",
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
								lineHeight: 1.5,
								maxHeight: 160,
								overflowY: "auto"
							}}
						>
							{props.episodeContent}
						</div>
					</ModalSectionCard>
				</div>
			}
			countdown={2}
			requireReason={false}
			confirmLabel="연결 끊기"
			tone="warning"
			onClose={props.onClose}
			onConfirm={props.onConfirm}
		/>
	)
}

function ModalSectionCard(props: { readonly label: string; readonly children: React.ReactNode }) {
	return (
		<div
			style={{
				background: "var(--pk-bg-sunken)",
				border: "1px solid var(--pk-border-subtle)",
				borderRadius: "var(--pk-radius-md)",
				padding: "var(--pk-space-3)"
			}}
		>
			<div
				style={{
					fontSize: "0.62rem",
					color: "var(--pk-text-muted)",
					marginBottom: 6,
					letterSpacing: "0.08em",
					textTransform: "uppercase",
					fontWeight: 700
				}}
			>
				{props.label}
			</div>
			{props.children}
		</div>
	)
}

/** 노드 id 를 짧은 kind + id tail 로 정리해 작은 pill 로 표시. */
function NodeChip({ id }: { readonly id: string }) {
	const parts = id.split(":")
	const kind = parts.length >= 3 ? parts.slice(0, -1).join(":") : ""
	const tail = parts[parts.length - 1] ?? id
	return (
		<span className="shape-flow__node" title={id}>
			{kind && (
				<span
					style={{
						fontSize: "0.65rem",
						color: "var(--pk-text-muted)",
						fontFamily: "var(--pk-font-mono)",
						marginRight: 4
					}}
				>
					{kind}
				</span>
			)}
			<span className="shape-flow__label">{tail}</span>
		</span>
	)
}
